import { useRef, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from './icons';
import { useDialog } from './useDialog';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Rendered as a sticky action row at the bottom, e.g. the filter buttons. */
  footer?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: ModalProps): ReactElement | null {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useDialog(open, onClose, panelRef);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#5b6164]/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="app-shell flex min-h-0 flex-1 flex-col pt-6 pb-6 outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-fog-100">{title}</h2>
            {subtitle !== undefined && <p className="mt-1 text-fog-300">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-mt-1 shrink-0 text-fog-100 transition-opacity hover:opacity-70"
          >
            <CloseIcon className="size-8" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer !== undefined && <div className="mt-4 shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
